import {
  type Event,
  type EventMenuLine,
  type EventStatus,
  type EventType,
  newId,
  type Rial,
} from "@kitchen/schemas";
import {
  Button,
  Chip,
  faDigits,
  Field,
  formatDate,
  formatMoney,
  formatTime,
  Icon,
  Panel,
  Seg,
} from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, eventsApi, type SiteRow } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { useOrgs } from "../menu/data.js";
import { ErrorLine, useAction } from "../common.js";
import { EventDetailsModal } from "./EventDetailsModal.js";
import { EventTypesTab } from "./EventTypesTab.js";

const fmtDate = (iso: string) => formatDate(iso.slice(0, 10));

type EventsMainTab = "events" | "types";

const STATUS_LABELS: Record<
  EventStatus,
  { label: string; tone?: "ok" | "warn" | "coral" | "blue" | "dark" }
> = {
  draft: { label: "پیش‌نویس", tone: "dark" },
  confirmed: { label: "قطعی", tone: "blue" },
  preparation: { label: "آماده‌سازی", tone: "warn" },
  event_day: { label: "روز مراسم", tone: "ok" },
  settlement: { label: "تسویه", tone: "blue" },
  archived: { label: "بایگانی", tone: "dark" },
  cancelled: { label: "لغوشده", tone: "coral" },
};

export function EventsPage() {
  const [tab, setTab] = useState<EventsMainTab>("events");

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="row wrap" style={{ alignItems: "center" }}>
        <Seg<EventsMainTab>
          label="بخش‌های مراسم"
          value={tab}
          onChange={setTab}
          options={[
            { value: "events", label: "مراسم‌ها و تقویم" },
            { value: "types", label: "انواع مراسم و قالب‌های کار" },
          ]}
        />
      </div>

      {tab === "events" && <EventsListTab />}
      {tab === "types" && <EventTypesTab />}
    </div>
  );
}

function EventsListTab() {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const {
    data: events,
    isLoading,
    error,
  } = useQuery<Event[]>({
    queryKey: ["events", statusFilter],
    queryFn: () =>
      eventsApi.listEvents(statusFilter === "all" ? undefined : { status: statusFilter as EventStatus }),
  });

  const { data: types } = useQuery<EventType[]>({
    queryKey: ["events", "types"],
    queryFn: () => eventsApi.listEventTypes(),
  });

  if (selectedEventId) {
    return (
      <EventDetailsModal
        eventId={selectedEventId}
        onClose={() => {
          setSelectedEventId(null);
        }}
      />
    );
  }

  const filteredEvents = events ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Action and filter bar */}
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "center" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--muted)" }}>فیلتر وضعیت:</span>
          <select
            className="input"
            style={{ width: "auto" }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
            }}
          >
            <option value="all">همه وضعیت‌ها</option>
            <option value="draft">پیش‌نویس</option>
            <option value="confirmed">قطعی</option>
            <option value="preparation">آماده‌سازی</option>
            <option value="event_day">روز مراسم</option>
            <option value="settlement">تسویه</option>
            <option value="archived">بایگانی</option>
            <option value="cancelled">لغوشده</option>
          </select>
        </div>

        <Button
          variant="primary"
          icon="plus"
          onClick={() => {
            setShowCreateModal(true);
          }}
        >
          ثبت مراسم جدید
        </Button>
      </div>

      <ErrorLine error={error instanceof Error ? error.message : null} />

      {/* New Event Form / Modal */}
      {showCreateModal && (
        <CreateEventModal
          eventTypes={types ?? []}
          onClose={() => {
            setShowCreateModal(false);
          }}
          onCreated={(id) => {
            setShowCreateModal(false);
            setSelectedEventId(id);
          }}
        />
      )}

      {/* Events List */}
      {isLoading ? (
        <p className="muted">در حال بارگذاری لیست مراسم‌ها...</p>
      ) : filteredEvents.length === 0 ? (
        <Panel>
          <div className="stack" style={{ alignItems: "center", padding: "var(--s5)", gap: "var(--s2)" }}>
            <Icon name="calendar" />
            <p className="muted" style={{ margin: 0 }}>
              مراسمی با فیلتر انتخابی یافت نشد.
            </p>
            <Button
              variant="default"
              onClick={() => {
                setShowCreateModal(true);
              }}
            >
              ثبت اولین مراسم
            </Button>
          </div>
        </Panel>
      ) : (
        <div className="stack" style={{ gap: "var(--s3)" }}>
          {filteredEvents.map((ev) => {
            const progressPercent = Math.round(ev.progressPerMille / 10);
            return (
              <div
                key={ev.id}
                className="card stack"
                style={{
                  gap: "var(--s3)",
                  padding: "var(--s4)",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setSelectedEventId(ev.id);
                }}
              >
                <div
                  className="row wrap"
                  style={{ justifyContent: "space-between", alignItems: "flex-start" }}
                >
                  <div>
                    <div
                      className="row"
                      style={{ alignItems: "center", gap: "var(--s2)", marginBottom: "4px" }}
                    >
                      <h3 style={{ margin: 0, fontWeight: 700, fontSize: "16px" }}>{ev.name}</h3>
                      <Chip tone={STATUS_LABELS[ev.status].tone}>{STATUS_LABELS[ev.status].label}</Chip>
                    </div>
                    <div className="muted" style={{ fontSize: "13px" }}>
                      مشتری: <strong>{ev.customerName}</strong> ({faDigits(ev.customerPhone)}) | محل:{" "}
                      <strong>{ev.place}</strong> | تعداد مهمان:{" "}
                      <strong>{faDigits(ev.guestCount)} نفر</strong>
                    </div>
                  </div>

                  <div style={{ textAlign: "left", direction: "ltr" }}>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>{fmtDate(ev.startAt)}</div>
                    <div className="muted" style={{ fontSize: "12px" }}>
                      {formatTime(ev.startAt)} تا {formatTime(ev.endAt)}
                    </div>
                  </div>
                </div>

                {/* Progress bar (§13.5) */}
                <div>
                  <div
                    className="row"
                    style={{ justifyContent: "space-between", fontSize: "11px", marginBottom: "3px" }}
                  >
                    <span className="muted">پیشرفت کارهای اجرایی:</span>
                    <span style={{ fontWeight: 600 }}>{faDigits(progressPercent)}٪</span>
                  </div>
                  <div
                    style={{
                      height: "6px",
                      background: "var(--line)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${String(progressPercent)}%`,
                        height: "100%",
                        background: progressPercent === 100 ? "var(--ok)" : "var(--blue-strong)",
                      }}
                    />
                  </div>
                </div>

                <div
                  className="row wrap"
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: "1px solid var(--card-line)",
                    paddingTop: "var(--s2)",
                    fontSize: "12px",
                  }}
                >
                  <div className="muted">
                    آشپزخانه: <strong>{ev.kitchenName}</strong> | بیعانه:{" "}
                    <strong>{formatMoney(ev.depositAmount)}</strong>
                  </div>
                  <Button variant="ghost" size="md">
                    مشاهده جزئیات و کارها ←
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==========================================
// Create Event Modal / Form
// ==========================================
function CreateEventModal({
  eventTypes,
  onClose,
  onCreated,
}: {
  eventTypes: EventType[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const refresh = [["events"]];

  const { data: kitchens } = useQuery<SiteRow[]>({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });

  const { orgs } = useOrgs();
  const today = tehranToday();
  const [eventTypeId, setEventTypeId] = useState<string>(eventTypes[0]?.id ?? "");
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [startTime, setStartTime] = useState("18:00");
  const [endDate, setEndDate] = useState(today);
  const [endTime, setEndTime] = useState("23:00");
  const [place, setPlace] = useState("");
  const [guestCount, setGuestCount] = useState("200");
  const [kitchenId, setKitchenId] = useState<string>("");
  const [depositToman, setDepositToman] = useState("10000000"); // 10 million toman
  const [menuFoodName, setMenuFoodName] = useState("زرشک‌پلو با مرغ مجلسی");
  const [menuPortions, setMenuPortions] = useState("200");
  const [menuNotes, setMenuNotes] = useState("همراه با دورچین و دسر");
  const [needsText, setNeedsText] = useState("سیستم صوت و نور\nظروف هتلی درجه یک\nمیهمانداران لباس فرم");
  const [notes, setNotes] = useState("");

  // Default kitchen
  const activeKitchens = (kitchens ?? []).filter((k) => k.kind === "kitchen");
  const selectedKitchen = activeKitchens.find((k) => k.id === (kitchenId || activeKitchens[0]?.id));

  const selectedType = eventTypes.find((t) => t.id === eventTypeId);

  const createAction = useAction(async () => {
    if (!name.trim() || !customerName.trim() || !place.trim()) return;

    const startAt = new Date(`${startDate}T${startTime}:00+03:30`).toISOString();
    const endAt = new Date(`${endDate}T${endTime}:00+03:30`).toISOString();

    const menuLines: EventMenuLine[] = menuFoodName.trim()
      ? [
          {
            foodId: newId(),
            foodName: menuFoodName.trim(),
            portions: Number(menuPortions) || Number(guestCount) || 100,
            pricePerPortion: 1_500_000 as Rial,
          },
        ]
      : [];

    const needs = needsText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    const depositAmount = ((Number(depositToman.replace(/[٬,]/g, "")) || 0) * 10) as Rial; // Toman to Rial
    const resolvedCustomerId = customerId || orgs[0]?.id || newId();
    const resolvedKitchenId = selectedKitchen?.id ?? newId();

    const ev = await eventsApi.createEvent({
      name: name.trim(),
      eventTypeId: eventTypeId || null,
      customerId: resolvedCustomerId,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim() || "09120000000",
      startAt,
      endAt,
      place: place.trim(),
      guestCount: Number(guestCount) || 100,
      kitchenId: resolvedKitchenId,
      kitchenName: selectedKitchen?.name ?? "آشپزخانه مرکزی",
      menuLines,
      needs,
      depositAmount,
      notes: notes.trim(),
    });

    onCreated(ev.id);
  }, refresh);

  return (
    <Panel title="ثبت مراسم و تشریفات جدید">
      <div className="stack" style={{ gap: "var(--s4)" }}>
        {/* Event Type Selector */}
        <div
          className="row wrap"
          style={{
            gap: "var(--s3)",
            background: "var(--card)",
            padding: "var(--s3)",
            borderRadius: "var(--r-card)",
          }}
        >
          <div style={{ flex: 1, minWidth: "220px" }}>
            <div className="field">
              <label htmlFor="ev-type">نوع مراسم (قالب کارهای خودکار)</label>
              <select
                id="ev-type"
                className="input"
                value={eventTypeId}
                onChange={(e) => {
                  setEventTypeId(e.target.value);
                }}
              >
                <option value="">(مراسم بدون قالب / سفارشی)</option>
                {eventTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({faDigits(t.taskTemplates.length)} کار پیش‌فرض)
                  </option>
                ))}
              </select>
            </div>
          </div>
          {selectedType && (
            <div style={{ flex: 2, minWidth: "250px", fontSize: "12px", color: "var(--muted)" }}>
              با انتخاب این قالب، <strong>{faDigits(selectedType.taskTemplates.length)} کار</strong> بر اساس
              ساعت شروع مراسم به‌طور خودکار ایجاد خواهند شد.
            </div>
          )}
        </div>

        {/* Customer Org Selector */}
        {orgs.length > 0 && (
          <div className="field">
            <label htmlFor="ev-cust-org">انتخاب مشتری سازمانی (اختیاری)</label>
            <select
              id="ev-cust-org"
              className="input"
              value={customerId}
              onChange={(e) => {
                const id = e.target.value;
                setCustomerId(id);
                const found = orgs.find((o) => o.id === id);
                if (found) setCustomerName(found.name);
              }}
            >
              <option value="">(مشتری حقیقی یا وارد کردن دستی)</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Main Info */}
        <div className="row wrap" style={{ gap: "var(--s2)" }}>
          <div style={{ flex: 2, minWidth: "220px" }}>
            <Field
              id="ev-name"
              label="عنوان مراسم *"
              placeholder="مثلاً: جشن عقد پرنیان و نوید"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <Field
              id="ev-cust-name"
              label="نام مشتری / طرف قرارداد *"
              placeholder="نام و نام خانوادگی"
              value={customerName}
              onChange={(e) => {
                setCustomerName(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "140px" }}>
            <Field
              id="ev-cust-phone"
              label="تلفن تماس مشتری"
              placeholder="0912..."
              value={customerPhone}
              onChange={(e) => {
                setCustomerPhone(e.target.value);
              }}
            />
          </div>
        </div>

        {/* Date, Time, Place */}
        <div className="row wrap" style={{ gap: "var(--s2)" }}>
          <div style={{ flex: 1, minWidth: "140px" }}>
            <Field
              id="ev-start-date"
              label="تاریخ برگزاری"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setEndDate(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "110px" }}>
            <Field
              id="ev-start-time"
              label="ساعت شروع"
              type="time"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "110px" }}>
            <Field
              id="ev-end-time"
              label="ساعت پایان"
              type="time"
              value={endTime}
              onChange={(e) => {
                setEndTime(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 2, minWidth: "200px" }}>
            <Field
              id="ev-place"
              label="محل برگزاری (آدرس یا نام سالن) *"
              placeholder="مثلاً: باغ تالار سپید، سالن شماره ۲"
              value={place}
              onChange={(e) => {
                setPlace(e.target.value);
              }}
            />
          </div>
        </div>

        {/* Guest Count, Kitchen, Deposit */}
        <div className="row wrap" style={{ gap: "var(--s2)" }}>
          <div style={{ flex: 1, minWidth: "120px" }}>
            <Field
              id="ev-guest"
              label="تعداد مهمانان (نفر)"
              type="number"
              value={guestCount}
              onChange={(e) => {
                setGuestCount(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <div className="field">
              <label htmlFor="ev-kitchen">آشپزخانه مسئول پخت</label>
              <select
                id="ev-kitchen"
                className="input"
                value={kitchenId || selectedKitchen?.id || ""}
                onChange={(e) => {
                  setKitchenId(e.target.value);
                }}
              >
                {activeKitchens.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <Field
              id="ev-deposit"
              label="مبلغ بیعانه (تومان)"
              value={depositToman}
              onChange={(e) => {
                setDepositToman(e.target.value);
              }}
            />
          </div>
        </div>

        {/* Menu & Needs */}
        <div
          className="stack"
          style={{
            gap: "var(--s2)",
            background: "var(--panel)",
            padding: "var(--s3)",
            borderRadius: "var(--r-card)",
          }}
        >
          <h4 style={{ margin: 0, fontWeight: 600 }}>منوی غذایی و خدمات مورد نیاز</h4>
          <div className="row wrap" style={{ gap: "var(--s2)" }}>
            <div style={{ flex: 2, minWidth: "200px" }}>
              <Field
                id="ev-menu-food"
                label="غذای اصلی"
                value={menuFoodName}
                onChange={(e) => {
                  setMenuFoodName(e.target.value);
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: "100px" }}>
              <Field
                id="ev-menu-portions"
                label="تعداد پرس"
                type="number"
                value={menuPortions}
                onChange={(e) => {
                  setMenuPortions(e.target.value);
                }}
              />
            </div>
            <div style={{ flex: 2, minWidth: "180px" }}>
              <Field
                id="ev-menu-notes"
                label="توضیحات سرو"
                value={menuNotes}
                onChange={(e) => {
                  setMenuNotes(e.target.value);
                }}
              />
            </div>
          </div>

          <Field
            id="ev-needs"
            label="ملزومات و خدمات خاص (هر مورد در یک خط)"
            placeholder="سیستم صوت&#10;ظروف کریستال"
            value={needsText}
            onChange={(e) => {
              setNeedsText(e.target.value);
            }}
          />
        </div>

        <Field
          id="ev-notes"
          label="یادداشت‌های هماهنگی"
          placeholder="توضیحات و الزامات خاص مراسم..."
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
          }}
        />

        <ErrorLine error={createAction.error} />

        <div className="row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
          <Button variant="ghost" onClick={onClose}>
            انصراف
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={!name.trim() || !customerName.trim() || !place.trim() || createAction.busy}
            onClick={() => {
              createAction.run({});
            }}
          >
            ثبت مراسم و تولید کارها
          </Button>
        </div>
      </div>
    </Panel>
  );
}
